<template>
  <component-wrapper class="newsroom-group-page-fi">
    <grid-container-fi
      class="newsroom-group-page-fi__grid-container"
      :container="false"
    >
      <div class="newsroom-group-page-fi__headlines">
        <headline-fi
          v-if="fields.headline?.value"
          class="newsroom-group-page-fi__headline"
          tag="h1"
          :size="60"
        >
          {{ fields.headline.value }}
        </headline-fi>
      </div>
      <div class="newsroom-group-page-fi__all-news">
        <loading-indicator
          v-if="isLoading"
          variant="spinnerLocal"
          class="spinner-local--rebrush newsroom-group-page-fi__loading-indicator"
        />

        <teaser-big-image-element-fi
          v-for="(news, index) in categoryPressNews"
          :key="news.category"
          :image="news.image ? { src: news.image } : null"
          :image-pos-right="index % 2 === 0"
          :bg-color="index % 2 === 0 ? 'white' : 'light'"
        >
          <template #content>
            <span class="badge-fi">
              {{ news.date }}
            </span>
            <h3 v-if="news.headline">
              {{ news.headline }}
            </h3>
            <p v-if="news.paragraph">
              {{ news.paragraph }}
            </p>
            <a
              v-if="news.link && teaserButtonText?.length > 0"
              :href="news.link"
              class="button-fi__tertiary"
            >
              {{ teaserButtonText }}
            </a>
          </template>
        </teaser-big-image-element-fi>

        <section-divider-fi />

        <pagination-progress-bar
          :pagination-page="page + 1"
          :pagination-total="newsCount"
          :progressbar-value="pagingPercentage"
          :disabled-state="isLoading"
          :show-more-button="categoryPressNews.length < newsCount"
          :query-params="null"
          @loadmore="fetchMore"
        >
          <template v-if="(page + 1) * pageSize <= newsCount">
            <span>{{
              $dict.get('product-List.Pagination.Show-Sum', [(page + 1) * pageSize, newsCount])
            }}</span>
          </template>

          <template v-else>
            <span>{{ $dict.get('product-List.Pagination.Show-Sum', [newsCount, newsCount]) }}</span>
          </template>
        </pagination-progress-bar>

        <section-divider-fi />
      </div>
    </grid-container-fi>
  </component-wrapper>
</template>

<script>
import { HeadlineFi, LoadingIndicator, PaginationProgressBar } from 'atoms';
import axios from 'axios';
import ComponentWrapper from 'components/component-wrapper/ComponentWrapper';
import GridContainerFi from 'components/grid-fi/GridContainerFi';
import TeaserBigImageElementFi from 'components/teaser/teaser-big-image-element-fi/TeaserBigImageElementFi';
import SectionDividerFi from 'pages/section-divider-fi/SectionDividerFi';
import { useSSRContext } from 'vue';

export default {
  name: 'NewsroomGroupPageFi',
  components: {
    HeadlineFi,
    ComponentWrapper,
    GridContainerFi,
    TeaserBigImageElementFi,
    SectionDividerFi,
    LoadingIndicator,
    PaginationProgressBar,
  },
  inject: ['fields', 'prefetch'],
  data() {
    return {
      isLoading: true,
      newsCount: 0,
      page: 0,
      pageSize: 5,
      categoryPressNews: [],
    };
  },
  computed: {
    apiUrl() {
      const pressReleasesUrl = IS_SHOWROOM
        ? '/__mocks__/newsroom/newsroom-category.json'
        : `${this.$fischer.hostUrlBase()}/api/press/get?lang=${this.$fischer.siteLanguage()}&category=${this.$fischer.pageId()}&pagesize=${
            this.pageSize
          }&page=${this.page}`;

      return pressReleasesUrl;
    },
    pagingPercentage() {
      return (100 * this.categoryPressNews.length) / this.newsCount;
    },
    teaserButtonText() {
      return this.$dict.get('newsroom.Group-Page.Teaser-Button-Text');
    },
  },
  async serverPrefetch() {
    this.getUrlPageParam();
    this.ssrPrefetch = await this.fetchData();
    if (this.ssrPrefetch) {
      await this.getCategoryPressNews();
      this.ctx.prefetch = { Data: this.ssrPrefetch.data?.Data };
    }
  },
  created() {
    if (IS_SSR) {
      this.ctx = useSSRContext() || {};
    } else {
      this.getCategoryPressNews();
    }
  },
  mounted() {
    this.$store.dispatch('navigation/setBackLink', {
      text: this.$dict.get('my-Account.Address-Overview.Back-To-Dashboard'),
      url: this.$fischer.pages.myAccount().url,
    });
    this.getUrlPageParam();
  },
  methods: {
    getUrlPageParam() {
      const page = this.$fischer.query('page');
      if (page) {
        this.page = Number.parseInt(page, 10) - 1;
      }
    },
    fetchMore() {
      if (this.$fischer.query('page')) {
        window.location.search = String(window.location.search).replace(
          /\bpage=\d+/,
          `page=${this.page + 1}`,
        );
        return;
      }
      this.page += 1;
      return this.getCategoryPressNews(true);
    },
    fetchData() {
      return axios.get(this.apiUrl);
    },
    // NOTE: do not convert to async, so rehydration can be done without scheduling a task
    fetchPressReleases(isFetchMore) {
      if (!IS_SSR && !isFetchMore && this.prefetch) {
        return { then: (callback) => callback(this.prefetch) };
      }
      if (IS_SSR) {
        return { then: (callback) => callback(this.ssrPrefetch?.data || {}) };
      }
      return this.fetchData().then((result) => ({ then: (callback) => callback(result.data) }));
    },
    getCategoryPressNews(isFetchMore = false) {
      this.isLoading = true;

      this.fetchPressReleases(isFetchMore).then((pressResult) => {
        const getLocalDate = (date) =>
          date
            ? new Date(date).toLocaleDateString(this.$fischer.siteLanguage(), {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })
            : '';

        const categoryPressNews = pressResult?.Data?.PressReleasesList?.map((n) => ({
          headline: n.Document.TeaserHeadline,
          date: getLocalDate(n.Document.Date),
          paragraph: n.Document.TeaserText,
          image: n.Document.TeaserImage,
          link: n.Document.ItemLink,
          lang: n.Document.Language,
          category: n.Document.Category,
        }));

        this.newsCount = pressResult?.Data?.TotalSearchResults || 0;

        if (isFetchMore) {
          this.categoryPressNews = [...this.categoryPressNews, ...(categoryPressNews || [])];
        } else {
          this.categoryPressNews = categoryPressNews || [];
        }

        this.isLoading = false;
      });
    },
  },
};
</script>

<style lang="scss">
@import './newsroom-group-page-fi-unscoped';
</style>

<style scoped lang="scss">
@import './newsroom-group-page-fi';
</style>
